import { z } from 'zod';
import { RequestStatus } from '../requests/status.js';

// WHY (#1596): reverse-read contract for `GET /leads/:id/requests` and
// `GET /deals/:id/requests`. The endpoints return the actor-visible China
// `Request` rows linked to a parent Lead/Deal. The summary is intentionally
// NON-PII and minimal — id, public code, status, SLA deadline — so a CRM
// actor with parent visibility can list linked requests without widening
// into the full request read model. The parent-visibility check runs FIRST
// server-side (404 on an invisible parent), then the per-request visibility
// predicate filters the rows, so cross-manager requests stay hidden.

/**
 * @aiPurpose minimal, PII-safe summary of one China `Request` linked to a
 *   Lead/Deal, returned by the reverse-read endpoints (#1596).
 * @aiInvariant `id` is the server-minted `LR-#####` public code (the
 *   request row's primary key is its human public code — there is no
 *   separate column), so `publicCode` carries the same value. `status`
 *   reuses the canonical `RequestStatus`; `slaDeadline` is nullable.
 * @aiSecurity exposes NO request body fields (name, route, cargo, parties)
 *   — only the four reference fields. Reverse-read rows are already filtered
 *   by the actor's request visibility scope before this shape is built.
 */
export const LinkedRequestSummarySchema = z
  .object({
    id: z.string().regex(/^LR-\d{5}$/, 'must match LR-NNNNN'),
    publicCode: z.string().regex(/^LR-\d{5}$/, 'must match LR-NNNNN'),
    status: z.nativeEnum(RequestStatus),
    slaDeadline: z.string().datetime().nullable(),
  })
  .strict();
export type LinkedRequestSummary = z.infer<typeof LinkedRequestSummarySchema>;

/**
 * @aiPurpose response for `GET /leads/:id/requests` and
 *   `GET /deals/:id/requests` (#1596) — the actor-visible linked-request
 *   summaries for a parent the actor can already see.
 */
export const LinkedRequestListResponseSchema = z
  .object({
    items: z.array(LinkedRequestSummarySchema),
  })
  .strict();
export type LinkedRequestListResponse = z.infer<typeof LinkedRequestListResponseSchema>;
